/**
 * Coach System Zustand Store V2
 *
 * Simplified coach system:
 * - Anyone can invite anyone as coach
 * - Coach profiles are OPTIONAL
 * - "isActingAsCoach" means user has clients or pending invitations
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  coachProfile,
  userSearch,
  clientRelationships,
  coachRelationships,
  coachNotes,
} from '../services/coachService';
import toast from 'react-hot-toast';

// Specialization options for fitness coaches
export const COACH_SPECIALIZATIONS = {
  strength: { label: 'Strength Training', emoji: '💪' },
  cardio: { label: 'Cardio', emoji: '🏃' },
  weight_loss: { label: 'Weight Loss', emoji: '⚖️' },
  muscle_building: { label: 'Muscle Building', emoji: '🏋️' },
  flexibility: { label: 'Flexibility', emoji: '🧘' },
  functional: { label: 'Functional Training', emoji: '🎯' },
  sports: { label: 'Sports Performance', emoji: '🏆' },
  rehabilitation: { label: 'Rehabilitation', emoji: '🏥' },
};

// Note type configuration
export const NOTE_TYPES = {
  feedback: { label: 'Geri Bildirim', emoji: '💬', color: 'blue' },
  suggestion: { label: 'Öneri', emoji: '💡', color: 'yellow' },
  warning: { label: 'Uyarı', emoji: '⚠️', color: 'red' },
  praise: { label: 'Tebrik', emoji: '🎉', color: 'green' },
};

export const useCoachStore = create(
  subscribeWithSelector((set, get) => ({
    // =====================================================
    // STATE
    // =====================================================

    // Coach status - true if user has clients or pending invitations
    isActingAsCoach: false,
    hasClients: false,
    hasPendingInvitations: false,

    // Optional coach profile
    coachProfileData: null,
    hasCoachProfile: false,
    isLoadingCoachProfile: false,

    // For users acting as coach: their clients
    clients: [],
    isLoadingClients: false,
    pendingInvitations: [],
    isLoadingInvitations: false,

    // For users: their coaches
    myCoaches: [],
    isLoadingMyCoaches: false,

    // Notes
    myNotes: [], // Notes received from coaches (for clients)
    clientNotes: {}, // Notes per client (for coaches) - { clientId: notes[] }
    unreadNotesCount: 0,
    isLoadingNotes: false,

    // Selected client for coach view
    selectedClient: null,
    selectedClientWorkouts: [],
    isLoadingClientWorkouts: false,

    // Viewing mode - when coach is viewing a client's FitTrack
    viewingClientId: null,        // null = viewing own data, UUID = viewing client's data
    viewingClientInfo: null,      // { id, email, display_name } of the client being viewed

    // User search (for inviting as coach)
    userSearchResults: [],
    isSearchingUsers: false,

    // Initialization tracking
    _isInitialized: false,
    _isInitializing: false,
    _isCheckingCoachStatus: false,
    _unreadCountLoaded: false,
    _myCoachesLoaded: false,

    // Modals
    isInviteModalOpen: false,
    isAddNoteModalOpen: false,
    selectedUserForInvite: null,
    noteContext: null, // { clientId, workoutId?, workoutDate? }

    // =====================================================
    // INITIALIZATION
    // =====================================================

    /**
     * Initialize the coach store
     * Deduplicates calls - won't re-initialize if already done
     */
    initialize: async (force = false) => {
      const { _isInitialized, _isInitializing } = get();

      // Skip if already initialized (unless forced)
      if (!force && _isInitialized) {
        return;
      }

      // Skip if already initializing
      if (_isInitializing) {
        return;
      }

      set({ _isInitializing: true });

      const {
        checkCoachStatus,
        loadCoachProfile,
        loadMyCoaches,
        loadUnreadNotesCount,
      } = get();

      try {
        // Check if user is acting as coach (has clients/invitations)
        await checkCoachStatus(true); // force to bypass its own guard

        // Load optional coach profile
        await loadCoachProfile();

        // Load user's coaches (as a client)
        await loadMyCoaches();

        // Load unread notes count
        await loadUnreadNotesCount();

        set({ _isInitialized: true, _isInitializing: false });
      } catch (error) {
        console.error('Error initializing coach store:', error);
        set({ _isInitializing: false });
      }
    },

    /**
     * Check if user is acting as coach
     * Deduplicates calls - won't re-check if already checking
     */
    checkCoachStatus: async (force = false) => {
      const { _isCheckingCoachStatus } = get();

      // Skip if already checking (unless forced)
      if (!force && _isCheckingCoachStatus) {
        return;
      }

      set({ _isCheckingCoachStatus: true });

      try {
        const hasClients = await coachRelationships.hasClients();
        const hasPending = await coachRelationships.hasPendingInvitations();

        set({
          hasClients,
          hasPendingInvitations: hasPending,
          isActingAsCoach: hasClients || hasPending,
          _isCheckingCoachStatus: false,
        });

        // If acting as coach, load clients and invitations
        if (hasClients || hasPending) {
          get().loadClients();
          get().loadPendingInvitations();
        }
      } catch (error) {
        console.error('Error checking coach status:', error);
        set({ _isCheckingCoachStatus: false });
      }
    },

    // =====================================================
    // COACH PROFILE (OPTIONAL)
    // =====================================================

    /**
     * Load optional coach profile
     */
    loadCoachProfile: async () => {
      set({ isLoadingCoachProfile: true });
      try {
        const profile = await coachProfile.getOwn();
        set({
          hasCoachProfile: !!profile,
          coachProfileData: profile,
          isLoadingCoachProfile: false,
        });
      } catch (error) {
        console.error('Error loading coach profile:', error);
        set({ isLoadingCoachProfile: false });
      }
    },

    /**
     * Create coach profile (optional - for displaying info)
     */
    createCoachProfile: async (profileData) => {
      try {
        const profile = await coachProfile.create(profileData);
        set({
          hasCoachProfile: true,
          coachProfileData: profile,
        });
        toast.success('Koç profiliniz oluşturuldu!');
        return profile;
      } catch (error) {
        console.error('Error creating coach profile:', error);
        toast.error('Koç profili oluşturulamadı');
        throw error;
      }
    },

    /**
     * Update coach profile
     */
    updateCoachProfile: async (profileData) => {
      try {
        const profile = await coachProfile.update(profileData);
        set({ coachProfileData: profile });
        toast.success('Profil güncellendi');
        return profile;
      } catch (error) {
        console.error('Error updating coach profile:', error);
        toast.error('Profil güncellenemedi');
        throw error;
      }
    },

    // =====================================================
    // CLIENT RELATIONSHIPS (USER SIDE)
    // =====================================================

    /**
     * Load user's coaches
     */
    loadMyCoaches: async (force = false) => {
      const { isLoadingMyCoaches, _myCoachesLoaded } = get();

      // Skip if already loading
      if (isLoadingMyCoaches) return;

      // Skip if already loaded (unless forced)
      if (!force && _myCoachesLoaded) return;

      set({ isLoadingMyCoaches: true });
      try {
        const coaches = await clientRelationships.getMyCoaches();
        set({
          myCoaches: coaches,
          isLoadingMyCoaches: false,
          _myCoachesLoaded: true,
        });
      } catch (error) {
        console.error('Error loading my coaches:', error);
        set({ isLoadingMyCoaches: false });
      }
    },

    /**
     * Invite a user as coach
     */
    inviteCoach: async (coachUserId, permissions) => {
      try {
        const relationship = await clientRelationships.inviteCoach(coachUserId, permissions);
        set((state) => ({
          myCoaches: [relationship, ...state.myCoaches],
          isInviteModalOpen: false,
          selectedUserForInvite: null,
        }));
        toast.success('Davet gönderildi!');
        return relationship;
      } catch (error) {
        console.error('Error inviting coach:', error);
        if (error.message?.includes('unique') || error.message?.includes('duplicate')) {
          toast.error('Bu kişiye zaten davet göndermişsiniz');
        } else {
          toast.error('Davet gönderilemedi');
        }
        throw error;
      }
    },

    /**
     * Update coach permissions
     */
    updateCoachPermissions: async (relationshipId, permissions) => {
      try {
        const updated = await clientRelationships.updatePermissions(relationshipId, permissions);
        set((state) => ({
          myCoaches: state.myCoaches.map((c) =>
            c.id === relationshipId ? { ...c, ...updated } : c
          ),
        }));
        toast.success('İzinler güncellendi');
        return updated;
      } catch (error) {
        console.error('Error updating permissions:', error);
        toast.error('İzinler güncellenemedi');
        throw error;
      }
    },

    /**
     * Revoke coach access
     */
    revokeCoachAccess: async (relationshipId) => {
      try {
        await clientRelationships.revokeAccess(relationshipId);
        set((state) => ({
          myCoaches: state.myCoaches.filter((c) => c.id !== relationshipId),
        }));
        toast.success('Koç erişimi iptal edildi');
      } catch (error) {
        console.error('Error revoking access:', error);
        toast.error('Erişim iptal edilemedi');
        throw error;
      }
    },

    // =====================================================
    // COACH RELATIONSHIPS (COACH SIDE)
    // =====================================================

    /**
     * Load clients (for users acting as coach)
     */
    loadClients: async () => {
      set({ isLoadingClients: true });
      try {
        const clients = await coachRelationships.getMyClients();
        set({
          clients,
          isLoadingClients: false,
          hasClients: clients.length > 0,
        });
      } catch (error) {
        console.error('Error loading clients:', error);
        set({ isLoadingClients: false });
      }
    },

    /**
     * Load pending invitations (for users acting as coach)
     */
    loadPendingInvitations: async () => {
      set({ isLoadingInvitations: true });
      try {
        const invitations = await coachRelationships.getPendingInvitations();
        set({
          pendingInvitations: invitations,
          isLoadingInvitations: false,
          hasPendingInvitations: invitations.length > 0,
        });
      } catch (error) {
        console.error('Error loading invitations:', error);
        set({ isLoadingInvitations: false });
      }
    },

    /**
     * Accept an invitation
     */
    acceptInvitation: async (relationshipId) => {
      try {
        const relationship = await coachRelationships.acceptInvitation(relationshipId);
        set((state) => ({
          pendingInvitations: state.pendingInvitations.filter((i) => i.id !== relationshipId),
          clients: [relationship, ...state.clients],
          hasPendingInvitations: state.pendingInvitations.length - 1 > 0,
          hasClients: true,
          isActingAsCoach: true,
        }));
        toast.success('Davet kabul edildi!');
        return relationship;
      } catch (error) {
        console.error('Error accepting invitation:', error);
        toast.error('Davet kabul edilemedi');
        throw error;
      }
    },

    /**
     * Reject an invitation
     */
    rejectInvitation: async (relationshipId) => {
      try {
        await coachRelationships.rejectInvitation(relationshipId);
        const newPending = get().pendingInvitations.filter((i) => i.id !== relationshipId);
        set((state) => ({
          pendingInvitations: newPending,
          hasPendingInvitations: newPending.length > 0,
          isActingAsCoach: state.hasClients || newPending.length > 0,
        }));
        toast.success('Davet reddedildi');
      } catch (error) {
        console.error('Error rejecting invitation:', error);
        toast.error('Davet reddedilemedi');
        throw error;
      }
    },

    /**
     * Load client workouts for coach view
     */
    loadClientWorkouts: async (clientId, startDate = null, endDate = null) => {
      set({ isLoadingClientWorkouts: true });
      try {
        const workouts = await coachRelationships.getClientWorkouts(clientId, startDate, endDate);
        set({
          selectedClientWorkouts: workouts,
          isLoadingClientWorkouts: false,
        });
        return workouts;
      } catch (error) {
        console.error('Error loading client workouts:', error);
        set({ isLoadingClientWorkouts: false });
        if (error.message?.includes('Access denied')) {
          toast.error('Bu öğrencinin antrenmanlarını görüntüleme izniniz yok');
        }
        throw error;
      }
    },

    /**
     * Select a client for detailed view
     */
    selectClient: (client) => {
      set({
        selectedClient: client,
        selectedClientWorkouts: [],
      });
    },

    // =====================================================
    // COACH NOTES
    // =====================================================

    /**
     * Load notes received from coaches (for clients)
     */
    loadMyNotes: async (force = false) => {
      const { isLoadingNotes, myNotes } = get();

      // Skip if already loading
      if (isLoadingNotes) return;

      // Skip if already loaded (unless forced)
      if (!force && myNotes.length > 0) return;

      set({ isLoadingNotes: true });
      try {
        const notes = await coachNotes.getMyNotes();
        set({
          myNotes: notes,
          isLoadingNotes: false,
        });
      } catch (error) {
        console.error('Error loading my notes:', error);
        set({ isLoadingNotes: false });
      }
    },

    /**
     * Load unread notes count
     */
    loadUnreadNotesCount: async (force = false) => {
      const { _unreadCountLoaded } = get();

      // Skip if already loaded (unless forced)
      if (!force && _unreadCountLoaded) return;

      try {
        const count = await coachNotes.getUnreadCount();
        set({ unreadNotesCount: count, _unreadCountLoaded: true });
      } catch (error) {
        console.error('Error loading unread notes count:', error);
      }
    },

    /**
     * Load notes for a specific client (for coaches)
     */
    loadClientNotes: async (clientId) => {
      try {
        const notes = await coachNotes.getForClient(clientId);
        set((state) => ({
          clientNotes: {
            ...state.clientNotes,
            [clientId]: notes,
          },
        }));
        return notes;
      } catch (error) {
        console.error('Error loading client notes:', error);
        throw error;
      }
    },

    /**
     * Add a note (for coaches)
     */
    addNote: async (clientId, noteData) => {
      try {
        const note = await coachNotes.create(clientId, noteData);
        set((state) => ({
          clientNotes: {
            ...state.clientNotes,
            [clientId]: [note, ...(state.clientNotes[clientId] || [])],
          },
          isAddNoteModalOpen: false,
          noteContext: null,
        }));
        toast.success('Not eklendi');
        return note;
      } catch (error) {
        console.error('Error adding note:', error);
        toast.error('Not eklenemedi');
        throw error;
      }
    },

    /**
     * Update a note
     */
    updateNote: async (noteId, noteData, clientId) => {
      try {
        const note = await coachNotes.update(noteId, noteData);
        set((state) => ({
          clientNotes: {
            ...state.clientNotes,
            [clientId]: state.clientNotes[clientId]?.map((n) =>
              n.id === noteId ? note : n
            ) || [],
          },
        }));
        toast.success('Not güncellendi');
        return note;
      } catch (error) {
        console.error('Error updating note:', error);
        toast.error('Not güncellenemedi');
        throw error;
      }
    },

    /**
     * Delete a note
     */
    deleteNote: async (noteId, clientId) => {
      try {
        await coachNotes.delete(noteId);
        set((state) => ({
          clientNotes: {
            ...state.clientNotes,
            [clientId]: state.clientNotes[clientId]?.filter((n) => n.id !== noteId) || [],
          },
        }));
        toast.success('Not silindi');
      } catch (error) {
        console.error('Error deleting note:', error);
        toast.error('Not silinemedi');
        throw error;
      }
    },

    /**
     * Mark a note as read (for clients)
     */
    markNoteAsRead: async (noteId) => {
      try {
        await coachNotes.markAsRead(noteId);
        set((state) => ({
          myNotes: state.myNotes.map((n) =>
            n.id === noteId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
          ),
          unreadNotesCount: Math.max(0, state.unreadNotesCount - 1),
        }));
      } catch (error) {
        console.error('Error marking note as read:', error);
      }
    },

    /**
     * Mark all notes as read
     */
    markAllNotesAsRead: async () => {
      try {
        await coachNotes.markAllAsRead();
        set((state) => ({
          myNotes: state.myNotes.map((n) => ({
            ...n,
            is_read: true,
            read_at: new Date().toISOString(),
          })),
          unreadNotesCount: 0,
        }));
      } catch (error) {
        console.error('Error marking all notes as read:', error);
      }
    },

    // =====================================================
    // USER SEARCH
    // =====================================================

    /**
     * Search users to invite as coach
     */
    searchUsers: async (searchQuery) => {
      set({ isSearchingUsers: true });
      try {
        const results = await userSearch.searchForInvite(searchQuery);
        set({
          userSearchResults: results,
          isSearchingUsers: false,
        });
        return results;
      } catch (error) {
        console.error('Error searching users:', error);
        set({ isSearchingUsers: false });
        throw error;
      }
    },

    /**
     * Clear search results
     */
    clearUserSearchResults: () => {
      set({ userSearchResults: [] });
    },

    // =====================================================
    // VIEWING MODE (Coach viewing client's FitTrack)
    // =====================================================

    /**
     * Set the client being viewed (for coach mode in FitTrack)
     * @param {string|null} clientId - Client user ID or null to view own data
     * @param {object|null} clientInfo - Client info { id, email, display_name }
     */
    setViewingClient: (clientId, clientInfo = null) => {
      set({
        viewingClientId: clientId,
        viewingClientInfo: clientInfo,
      });
    },

    /**
     * Clear viewing client (return to viewing own data)
     */
    clearViewingClient: () => {
      set({
        viewingClientId: null,
        viewingClientInfo: null,
      });
    },

    /**
     * Get client info by ID from the clients list
     */
    getClientById: (clientId) => {
      const { clients } = get();
      return clients.find((c) => c.client_id === clientId);
    },

    // =====================================================
    // MODALS
    // =====================================================

    openInviteModal: (user = null) => {
      set({
        isInviteModalOpen: true,
        selectedUserForInvite: user,
      });
    },

    closeInviteModal: () => {
      set({
        isInviteModalOpen: false,
        selectedUserForInvite: null,
      });
    },

    openAddNoteModal: (context = null) => {
      set({
        isAddNoteModalOpen: true,
        noteContext: context,
      });
    },

    closeAddNoteModal: () => {
      set({
        isAddNoteModalOpen: false,
        noteContext: null,
      });
    },

    // =====================================================
    // RESET
    // =====================================================

    reset: () => {
      set({
        isActingAsCoach: false,
        hasClients: false,
        hasPendingInvitations: false,
        coachProfileData: null,
        hasCoachProfile: false,
        clients: [],
        pendingInvitations: [],
        myCoaches: [],
        myNotes: [],
        clientNotes: {},
        unreadNotesCount: 0,
        selectedClient: null,
        selectedClientWorkouts: [],
        viewingClientId: null,
        viewingClientInfo: null,
        userSearchResults: [],
        isInviteModalOpen: false,
        isAddNoteModalOpen: false,
        selectedUserForInvite: null,
        noteContext: null,
      });
    },
  }))
);
